import { useFAQConfig, useMarketConfigs } from '../../features/settings';
import { formatDateTime } from '../../lib/format';

const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';
const textareaCls =
  'min-h-[180px] w-full rounded-md border border-gray-300 px-3 py-3 font-mono text-xs leading-5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';

export function FAQPage() {
  const {
    data,
    items,
    loading,
    saving,
    error,
    addItem,
    updateItem,
    removeItem,
    save,
    reload,
  } = useFAQConfig();
  const { items: markets } = useMarketConfigs();
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="text-sm text-gray-500">
            配置 App「我的」页面常见问题入口展示的问答内容。问题和答案均为纯文本。
          </p>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-700">
            启用的条目会在 App 中公开展示。每条问题和答案都必须填写。
          </div>
        </div>
        <button
          type="button"
          onClick={addItem}
          className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
        >
          新增问题
        </button>
      </div>

      <section className="space-y-4">
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-sm text-gray-500">
            暂无常见问题，点击「新增问题」开始配置。
          </div>
        ) : null}

        {items.map((item, index) => (
          <div key={`${item.id || 'new'}-${index}`} className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">问题 {index + 1}</h2>
                <p className="mt-1 text-xs text-gray-400">
                  {item.id ? `ID: ${item.id}` : '保存后由后端生成 ID'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={item.enabled}
                    onChange={(event) => updateItem(index, { enabled: event.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  启用
                </label>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 transition-colors hover:bg-red-50"
                >
                  删除
                </button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_180px_140px]">
              <div>
                <label className="mb-1 block text-sm text-gray-700">问题</label>
                <input
                  value={item.question}
                  onChange={(event) => updateItem(index, { question: event.target.value })}
                  className={inputCls}
                  placeholder="例如：如何提现？"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-700">地区</label>
                <select
                  value={item.market_id || 'CN'}
                  onChange={(event) => updateItem(index, { market_id: event.target.value })}
                  className={inputCls}
                >
                  {markets.map((market) => (
                    <option key={market.market_id} value={market.market_id}>
                      {market.market_id} · {market.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-700">排序值</label>
                <input
                  type="number"
                  value={item.sort_order}
                  onChange={(event) => updateItem(index, { sort_order: Number(event.target.value) })}
                  className={inputCls}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-1 block text-sm text-gray-700">答案</label>
              <textarea
                value={item.answer_html}
                onChange={(event) => updateItem(index, { answer_html: event.target.value })}
                className={textareaCls}
                spellCheck={false}
                placeholder="这里填写答案内容，支持换行"
              />
            </div>
          </div>
        ))}
      </section>

      <div className="sticky bottom-0 flex items-center gap-3 border-t border-gray-200 bg-gray-50/95 py-4 backdrop-blur">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
        >
          {saving && (
            <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          )}
          保存配置
        </button>
        {data?.updated_at ? (
          <span className="text-xs text-gray-400">
            最后更新：{formatDateTime(data.updated_at)}
            {data.updated_by != null ? ` · 操作人 ID：${data.updated_by}` : ''}
          </span>
        ) : null}
      </div>
    </div>
  );
}
